import { View } from "react-native";
import { useEffect, useState } from "react";
import RacecardAndResultWidgetTabs from "./shared/RacecardAndResultWidgetTabs";
import { APIResponse } from "../../../../types/responses/APIResponse";
import { fetchGet } from "../../../../client";
import RacecardAndResultWidgetSingleDay from "./RacecardAndResultWidgetSingleDay";
import { getWeekday } from "../../../../utils/TimeCalculator";
import RacecardAndResultWidgetLoader from "../../../States/Loading/Loaders/cms/RacecardAndResultWidgetLoader";
import { RectangleLoader } from "../../../States/Loading/Loaders";
import Gap from "../../../Gap";
import {
  RacecardRes,
  ResultRes,
} from "../../../../types/races/racecardAndResultWidget";
import { logEvent } from "../../../../client/analytics/trackPageView";

/**
 * RacecardAndResultWidgetMultiDay is an attachment component for displaying racecards and results for a racecourse across
 * multiple days
 *
 * The tabs are a stripped down version of the main Tabs component due to the style differences
 *
 * The component fetches data based on the provided ID and expects an array of races, which it groups by date to display in the tabs
 * Each tab corresponds to a single day and uses the RacecardAndResultWidgetSingleDay component
 *
 * The date is shown in the tab label (e.g. "MONDAY"), and not repeated in the single day component to avoid redundancy
 *
 * @param {string} id - the ID of the multi day attachment to fetch data for
 *
 * @returns {JSX.Element} The rendered component, or null if there's an error or no data
 */

const RacecardAndResultWidgetMultiDay = ({ id }: { id: string }) => {
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [data, setData] = useState<RacecardRes[] | ResultRes[]>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }
    const fetchData = async () => {
      try {
        const res: APIResponse<{ data: RacecardRes[] | ResultRes[] }> =
          await fetchGet(`widgets/racecard-and-result-widget/${id}`);
        if (res.data) {
          setData(res.data);
        } else setError("No data found");
      } catch (err) {
        setError(
          "Error loading race: " +
            (err instanceof Error ? err.message : "Unknown")
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  if (loading)
    return (
      <View style={{ width: "100%" }}>
        <RectangleLoader width="100%" height={30} />
        <Gap vertical />
        <RacecardAndResultWidgetLoader />
      </View>
    );

  if (error || !data?.length) return null;

  // group races by date
  const racesByDate: Record<string, any[]> = {};

  (data ?? []).forEach((race: any) => {
    const date = race?.meeting?.date;
    if (!date) return;

    //initialise once
    racesByDate[date] ||= [];
    racesByDate[date].push(race);
  });

  const tabs = Object.entries(racesByDate).map(([date, racesForDay]) => ({
    label: getWeekday(new Date(date)),
    child: (
      <View style={{ marginVertical: 10 }}>
        <RacecardAndResultWidgetSingleDay
          key={date}
          dataOverride={racesForDay}
          displayDateHeading={false}
          multiday
        />
      </View>
    ),
  }));

  const handleTabChange = (index: number) => {
    setSelectedTab(index);
    logEvent({ key: "widget-tab-date-click", data: {} });
  };

  return (
    <RacecardAndResultWidgetTabs
      key={`racecards-results-widget-multi-day-${id}`}
      items={tabs}
      selectedIndex={selectedTab}
      onTabChange={(index) => handleTabChange(index)}
    />
  );
};

export default RacecardAndResultWidgetMultiDay;
